import type {
  CalendarEvent,
  CreateCalendarEventRequest,
  UpdateCalendarEventRequest,
  ListEventsParams,
} from '@/types/calendar'
import { useAuthStore } from '@/stores/auth.store'
import { API_BASE_URL } from './api-config'

const API_URL = API_BASE_URL

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const accessToken = useAuthStore.getState().accessToken

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    },
    credentials: 'include',
  })

  const data = await response.json()

  if (!response.ok) {
    throw new ApiError(response.status, data.error || 'Request failed', data)
  }

  return data
}

function buildQueryString(params: ListEventsParams): string {
  const searchParams = new URLSearchParams()
  if (params.startDate) searchParams.set('startDate', params.startDate)
  if (params.endDate) searchParams.set('endDate', params.endDate)
  if (params.childId) searchParams.set('childId', params.childId)
  const query = searchParams.toString()
  return query ? `?${query}` : ''
}

export const calendarApi = {
  // List events for childminder
  async listEvents(params: ListEventsParams = {}): Promise<{ events: CalendarEvent[] }> {
    const query = buildQueryString(params)
    return fetchWithAuth<{ events: CalendarEvent[] }>(`/api/calendar/events${query}`)
  },

  // Get single event
  async getEvent(id: string): Promise<{ event: CalendarEvent }> {
    return fetchWithAuth<{ event: CalendarEvent }>(`/api/calendar/events/${id}`)
  },

  // Create event (childminder)
  async createEvent(data: CreateCalendarEventRequest): Promise<{ event: CalendarEvent }> {
    return fetchWithAuth<{ event: CalendarEvent }>('/api/calendar/events', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Update event
  async updateEvent(id: string, data: UpdateCalendarEventRequest): Promise<{ event: CalendarEvent }> {
    return fetchWithAuth<{ event: CalendarEvent }>(`/api/calendar/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  // Delete event
  async deleteEvent(id: string): Promise<{ success: boolean }> {
    return fetchWithAuth<{ success: boolean }>(`/api/calendar/events/${id}`, {
      method: 'DELETE',
    })
  },
}

export { ApiError }
